import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Zap, ShieldCheck, Smartphone } from 'lucide-react';
import UserHeader from '../../components/header/UserHeader';

const AboutPage = () => {
  const navigate = useNavigate();

  const buyerPoints = [
    'Discover local and online products',
    'Secure payment processing',
    'Real-time order tracking',
    'Customer reviews and ratings',
    '24/7 customer support'
  ];

  const sellerPoints = [
    'Easy product management',
    'Real-time order notifications',
    'Analytics and insights',
    'Secure payment processing',
    'Marketing tools and promotions'
  ];

  const techItems = [
    {
      icon: Zap,
      tint: 'bg-blue-50 text-blue-600',
      title: 'Real-time Updates',
      desc: 'Live order tracking and notifications'
    },
    {
      icon: ShieldCheck,
      tint: 'bg-green-50 text-green-600',
      title: 'Secure Platform',
      desc: 'Advanced security and data protection'
    },
    {
      icon: Smartphone,
      tint: 'bg-purple-50 text-purple-600',
      title: 'Mobile First',
      desc: 'Optimized for all devices'
    }
  ];

  return (
    <>
      <UserHeader />
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Page sub-header */}
        <div className="flex items-center gap-1.5 px-3 h-12 bg-white border-b border-gray-100">
          <button
            onClick={() => navigate(-1)}
            className="p-1 text-gray-700"
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <span className="text-[15px] font-semibold text-gray-900">About ZAMMER</span>
        </div>

        <div className="px-4 py-5 space-y-4">
          {/* Intro */}
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">About ZAMMER</h1>
            <p className="text-sm text-gray-500 mt-1">
              Your complete online marketplace solution.
            </p>
          </div>

          {/* Mission */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Our Mission</h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              ZAMMER is dedicated to creating a seamless marketplace experience that connects
              buyers and sellers in a secure, efficient, and user-friendly environment. We
              believe in empowering local businesses and providing consumers with access to
              quality products and services.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Our platform combines cutting-edge technology with intuitive design to deliver an
              exceptional e-commerce experience for everyone involved.
            </p>
          </div>

          {/* For Buyers */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-3">For Buyers</h3>
            <ul className="space-y-2">
              {buyerPoints.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-gray-900 mt-0.5">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* For Sellers */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-3">For Sellers</h3>
            <ul className="space-y-2">
              {sellerPoints.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-gray-900 mt-0.5">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Technology */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Our Technology</h2>
            <div className="space-y-4">
              {techItems.map(({ icon: Icon, tint, title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <div
                    className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${tint}`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className="text-sm text-gray-600">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
